import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  CircularProgress,
  Collapse,
  Fab,
  Badge,
  Zoom,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Group as GroupIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CardGiftcard as GiftIcon,
  Stars as StarsIcon
} from '@mui/icons-material';
import api from '../utils/api';
import analytics from '../services/analytics';
import { links } from '../config/links';
import { useCouponStatus } from '../hooks/useCouponStatus';

const AlphaReferralCardCompact = ({ subscriptionStatus }) => {
  const theme = useTheme();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { couponData } = useCouponStatus('ALPHA90');

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      const response = await api.get('/referral/stats');
      setReferralData(response.data);
    } catch (err) {
      console.error('Failed to load referral data:', err);
      setError('Failed to load referral information');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    const referralLink = `${window.location.origin}${referralData.referral_link}`;
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    
    analytics.trackAlphaReferralLinkShared(
      'copy',
      referralData.referrals_used,
      referralData.remaining_referrals,
      referralData.max_referrals
    );
  };

  const shareViaEmail = () => {
    const referralLink = `${window.location.origin}${referralData.referral_link}`;
    const subject = links.emailTemplates.referral.getEmailSubject();
    const body = links.emailTemplates.referral.getEmailBody(referralLink);
    
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    analytics.trackAlphaReferralLinkShared(
      'email',
      referralData.referrals_used,
      referralData.remaining_referrals,
      referralData.max_referrals
    );
  };

  const shareViaWhatsApp = () => {
    const referralLink = `${window.location.origin}${referralData.referral_link}`;
    const message = links.emailTemplates.referral.getWhatsAppMessage(referralLink);
    
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    
    analytics.trackAlphaReferralLinkShared(
      'whatsapp',
      referralData.referrals_used,
      referralData.remaining_referrals,
      referralData.max_referrals
    );
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (error || !referralData?.is_alpha_tester) {
    return null; // Don't show card if not an alpha tester
  }

  // Only show after user has used at least one trial exam
  if (!subscriptionStatus || subscriptionStatus.allowance_used < 1) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative', mt: 3, mb: 2 }}>
      {/* Collapsed state - compact button */}
      {!expanded && (
        <Zoom in={!expanded} timeout={300}>
          <Button
            onClick={() => setExpanded(true)}
            sx={{
              width: '100%',
              py: 1.5,
              px: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4]
              },
              transition: 'all 0.3s ease',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
                animation: 'shimmer 4s infinite',
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' }
                }
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge 
                  badgeContent={referralData.remaining_referrals} 
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.7rem'
                    }
                  }}
                >
                  <GiftIcon sx={{ fontSize: 22 }} />
                </Badge>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Share 90% OFF with friends
                    <StarsIcon sx={{ fontSize: 14, color: alpha(theme.palette.warning.main, 0.8) }} />
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Founder exclusive • {couponData && couponData.remaining_uses <= 5 
                      ? <span style={{ color: theme.palette.error.main, fontWeight: 600 }}>Only {couponData.remaining_uses} deals left!</span>
                      : `${referralData.remaining_referrals} invites left`}
                  </Typography>
                </Box>
              </Box>
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </Box>
          </Button>
        </Zoom>
      )}

      {/* Expanded state */}
      {expanded && (
        <Zoom in={expanded} timeout={300}>
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              position: 'relative'
            }}
          >
            {/* Close button */}
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                '&:hover': {
                  backgroundColor: theme.palette.background.paper
                }
              }}
            >
              <ExpandLessIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <GiftIcon color="primary" />
              <Typography variant="body1" fontWeight={600}>
                Share Founder Status
              </Typography>
              <Chip
                label={`${referralData.remaining_referrals} LEFT`}
                size="small"
                sx={{ 
                  fontWeight: 'bold',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.dark
                }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Friends get <strong>5 free exams</strong> + <strong>90% OFF</strong> for 3 months
              </Typography>
              {couponData && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      90% OFF deals: 
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {couponData.current_uses > 0 && (
                        <span style={{ textDecoration: 'line-through', color: theme.palette.text.disabled }}>
                          {couponData.max_uses}
                        </span>
                      )}
                      {' '}
                      <span style={{ color: couponData.remaining_uses <= 5 ? theme.palette.error.main : theme.palette.success.main }}>
                        {couponData.remaining_uses} left
                      </span>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    {[...Array(Math.min(couponData.max_uses, 10))].map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 20,
                          height: 3,
                          borderRadius: 1,
                          backgroundColor: index < couponData.current_uses 
                            ? alpha(theme.palette.text.disabled, 0.3)
                            : couponData.remaining_uses <= 5 
                              ? alpha(theme.palette.error.main, 0.7)
                              : alpha(theme.palette.success.main, 0.7),
                          position: 'relative',
                          overflow: 'hidden',
                          '&::after': index < couponData.current_uses ? {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: 1,
                            backgroundColor: theme.palette.text.disabled,
                            transform: 'translateY(-50%)'
                          } : {}
                        }}
                      />
                    ))}
                    {couponData.max_uses > 10 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        +{couponData.max_uses - 10}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Quick share buttons */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Tooltip title="Share on WhatsApp">
                <IconButton
                  onClick={shareViaWhatsApp}
                  sx={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1fb855',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <WhatsAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share via Email">
                <IconButton
                  onClick={shareViaEmail}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Referral link */}
            <Box
              sx={{
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 1,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {`${window.location.origin}${referralData.referral_link}`}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <IconButton
                  size="small"
                  onClick={copyToClipboard}
                  sx={{
                    backgroundColor: copied ? theme.palette.success.main : theme.palette.background.default,
                    color: copied ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: copied ? theme.palette.success.dark : theme.palette.action.hover
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        </Zoom>
      )}
    </Box>
  );
};

export default AlphaReferralCardCompact;
